import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

/**
 * Route-level auth guard.
 *
 * F89 (Phase I Wave 1.26) · fixed strict-equality bug on `adminOnly`
 * that was redirecting `super_admin` users to `/app/dashboard` silently.
 * We now defer the role-shape decision to `isAdminRole` (SSOT in
 * `data/v12_user_nav.js`), which accepts both `admin` and `super_admin`.
 */
export default function ProtectedRoute({ children, adminOnly = false }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div
                data-testid="app-loading"
                className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] text-text-secondary"
            >
                <div className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-[var(--color-primary)] animate-pulse-soft" />
                    <span className="text-sm tracking-wide">Loading…</span>
                </div>
            </div>
        );
    }

    if (!user) {
        // FASE I Round-3 · redirect to /login with the original path
        // preserved in `?redirect=` so LoginRoute can honour it after
        // successful auth. Previously bounced users to `/` which routed
        // through LandingRootRouter and performed a full SSR reload →
        // bad UX for app-area deep-links (/dashboard, /marketplace/v10,
        // /app/*).
        const target = encodeURIComponent(location.pathname + location.search);
        return (
            <Navigate
                to={`/login?redirect=${target}`}
                state={{ from: location }}
                replace
            />
        );
    }

    if (adminOnly && !isAdminRole(user.role)) {
        // F89 · fail loud so similar bugs never slip through silently again.
        toast.error("Permessi insufficienti · richiesto admin o super_admin");
        return <Navigate to="/app/dashboard" replace />;
    }

    return children;
}
